import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useFormik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";
import { getProfile, updateProfile, changePassword, getProvinces, logoutUser } from "../services/api";
import { useCredits } from "../context/CreditsContext";
import headerLogo from "../assets/header_logo.svg";
import { useAuth } from "../context/AuthContext";
import { FONTS, COLORS, BREAKPOINTS, SHADOWS } from "../utils/styleConstants";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";

// Validation schemas
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+[0-9]{2,3}[0-9]{9}$/;
const passwordRegex = /^[a-zA-Z0-9]{8,16}$/;
const companyCifRegex = /^[A-Z][0-9]{7}[0-9A-Z]$/; // 1 letra + 7 dígitos + 1 letra o número

const profileUpdateSchema = Yup.object().shape({
  email: Yup.string()
    .matches(emailRegex, "Escriba un correo válido")
    .required("Este campo es obligatorio"),
  mobile_phone: Yup.string()
    .optional()
    .matches(phoneRegex, "Formato: +34 seguido de 9 dígitos. Ej: +34612345678"),
  lawyer_membership_number: Yup.string().optional(),
  province_id: Yup.number().optional(),
  accepts_emails: Yup.boolean().optional(),
  // Campos de sociedad
  acts_on_behalf_of_company: Yup.boolean().optional(),
  company_name: Yup.string().when('acts_on_behalf_of_company', {
    is: true,
    then: (schema) => schema.required('La razón social es obligatoria'),
    otherwise: (schema) => schema.optional()
  }),
  company_tax_id: Yup.string().when('acts_on_behalf_of_company', {
    is: true,
    then: (schema) => schema
      .required('El NIF/CIF es obligatorio')
      .matches(companyCifRegex, 'Formato válido: letra + 7 números + letra o número (p.ej. B1234567C)'),
    otherwise: (schema) => schema.optional()
  })
});

const passwordChangeSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Este campo es obligatorio"),
  newPassword: Yup.string()
    .matches(
      passwordRegex,
      "Escribe una contraseña alfanumérica de entre 8 a 16 caracteres."
    )
    .required("Este campo es obligatorio"),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Las contraseñas no coinciden")
    .required("Este campo es obligatorio"),
});

// Styled components (similar to RegistrationForm)
const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: ${COLORS.SECONDARY};
  display: flex;
  box-shadow: ${SHADOWS.DEFAULT};
  overflow: hidden;
  height: 100vh;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: linear-gradient(157deg, #ffeee7 27%, #c1d6ef 70%);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: linear-gradient(157deg, #ffeee7 40%, #c1d6ef 100%);
  }
`;

const FormHeader = styled.div`
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1rem;
  align-items: start;
  flex-shrink: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 2rem;
  }
`;

const HeaderLogo = styled.img`
  height: 2.5rem;
  width: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormTitle = styled.h1`
  color: var(--primary-color);
  font-size: 2.5rem;
  font-weight: 600;
  font-family: "AtHauss", "Avenir Next", sans-serif;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    color: #ff7f50;
  }
`;

const FormSubtitle = styled.p`
  color: var(--accent-color);
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  font-weight: 400;
  font-style: bold;
  margin: 0;
  max-width: 37%;
  line-height: 1.4;
  white-space: pre-line;
  min-height: 2.8em;

  @media (max-width: 1200px) {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    line-height: 1.3;
    min-height: auto;
    margin-bottom: 1rem;
    color: #ff7f50;
  }
`;

// Credits counter styles  
const CreditsCounter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    order: -1;
    margin-bottom: 1rem;
  }
`;

const CreditsNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ff7057;
  font-family: ${FONTS.PRIMARY};
  text-align: right;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const CreditsLabel = styled.div`
  font-size: 0.8rem;
  color: #ff7057;
  font-family: ${FONTS.SECONDARY || 'sans-serif'};
  font-weight: 500;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 7rem;
  margin-bottom: 0.5rem;
  flex: 1;
  align-content: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-gap: 0;
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--secondary-color);
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 500;
  line-height: 1.3;
  white-space: normal;
  word-wrap: break-word;
  min-width: 120px;

  @media (max-width: 768px) {
    color: #080d44;
    font-size: 0.9rem;
    display: ${(props) => (props.hideOnMobile ? "none" : "block")};
    line-height: 1.4;
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 1rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: 1rem;

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.9rem;
  }
`;

const ReadOnlyInput = styled(Input)`
  color: #666;
  cursor: not-allowed;
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;
  min-height: 3.5rem;

  .input-row {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    min-height: 2.5rem;
  }

  ${Label} {
    margin-bottom: 0;
    flex: 1;
    max-width: 200px;
    padding-top: 0.25rem;
  }

  ${Input}, ${ReadOnlyInput} {
    flex: 1;
    border-bottom: none;
    margin-top: 0.25rem;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    min-height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      min-height: auto;
    }

    ${Label} {
      width: 100%;
      margin-bottom: 0.5rem;
      max-width: none;
      padding-top: 0;
    }

    ${Input}, ${ReadOnlyInput} {
      width: 100%;
      text-align: left;
      padding-left: 0;
      margin-top: 0;
    }

    border-bottom: 1px solid #bad1e6;
  }
`;

const ErrorMessage = styled.div`
  color: var(--accent-color);
  font-size: 0.75rem;
  position: absolute;
  bottom: 0.3rem;
  right: 1rem;
  margin: 0;

  @media (max-width: 768px) {
    bottom: -1.5rem;
    right: 0rem;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  height: 3rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const ToggleLabel = styled.div`
  font-size: 1rem;
  color: var(--secondary-color);
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 500;

  @media (max-width: 768px) {
    color: #080d44;
    font-size: 0.9rem;
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 40px;
  height: 20px;
  background-color: ${(props) =>
    props.checked ? "var(--secondary-color)" : "#ccc"};
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.3s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(props) => (props.checked ? "20px" : "2px")};
    width: 16px;
    height: 16px;
    background-color: white;
    border-radius: 50%;
    transition: left 0.3s;
  }
`;

const NoLabel = styled.span`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.7rem;
  color: white;
  font-weight: bold;
`;

const SearchInput = styled(Input)`
  width: 100%;
  border-bottom: none;
  padding: 0.75rem 0;
  margin-right: 1rem;
`;

const ProvinceDropdown = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div`
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: relative;
  margin-top: 2rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: white;
  color: #a8b9d5;
  border: 1px solid #dbe4f2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: #f5f8fd;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);
  
  &:hover {
    background-color: #ff5b40;
  }
  
  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const BackButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #dbe4f2;
  background-color: white;
  color: #a8b9d5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: #f5f8fd;
  }
`;

const ArrowIcon = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 8px solid #a8b9d5;
`;

const SectionTitle = styled.h2`
  color: var(--secondary-color);
  font-size: 1.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin-top: 2rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    color: #080d44;
    font-size: 1.3rem;
  }
`;

const PasswordField = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PasswordInput = styled(Input)`
  width: 100%;
  padding-right: 40px;
  border: none;
  margin-right: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
  }
`;

const EyeIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    right: 0;
  }
`;

const EyeIconSvg = ({ isVisible }) =>
  isVisible ? (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
        fill="currentColor"
      />
    </svg>
  );

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-family: ${FONTS.SECONDARY};
`;

const ErrorModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ErrorModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
`;

const ErrorModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ErrorModalTitle = styled.h3`
  color: var(--secondary-color);
  margin: 0;
  font-family: ${FONTS.PRIMARY};
`;

const ErrorModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
`;

const ErrorModalMessage = styled.p`
  color: #333;
  font-family: ${FONTS.SECONDARY};
`;

const SkeletonLoader = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '100%'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;
  min-height: 3.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #e0e0e0;

  .input-row {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    min-height: 2.5rem;
  }

  @media (max-width: 768px) {
    min-height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      min-height: auto;
    }
  }
`;

const LogoutButton = styled(Button)`
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);
  
  &:hover {
    background-color: #ff5b40;
  }
  
  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const { credits, formatCreditsDisplay } = useCredits();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  const [provinceFilter, setProvinceFilter] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [isLawyerMode, setIsLawyerMode] = useState(false);
  const [isCompanyMode, setIsCompanyMode] = useState(false);
  const provinceDropdownRef = useRef(null);



  // Fetch profile data and provinces on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileResponse, provincesResponse] = await Promise.all([
          getProfile(),
          getProvinces()
        ]);
        
        const userData = profileResponse.user || profileResponse;
        setProfileData(userData);
        setProvinces(provincesResponse);
        
        // Inicializar isLawyerMode basado en si el usuario ya tiene datos de abogado
        const hasLawyerData = userData?.lawyer_membership_number && userData.lawyer_membership_number.trim() !== "";
        setIsLawyerMode(hasLawyerData);

        // Inicializar modo empresa
        const hasCompanyData = userData?.acts_on_behalf_of_company === true || userData?.acts_on_behalf_of_company === 1;
        setIsCompanyMode(!!hasCompanyData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error al cargar los datos del perfil");
        setShowErrorModal(true);
      }
    };

    fetchData();
  }, []);



  // Profile update formik
  const profileFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: profileData?.email || "",
      mobile_phone: profileData?.mobile_phone || "",
      lawyer_membership_number: profileData?.lawyer_membership_number || "",
      province_id: profileData?.province_id || "",
      province_name: profileData?.province_name || "",
      accepts_emails: profileData?.accept_emails === true || profileData?.accept_emails === 1 || false,
      // Campos de sociedad
      acts_on_behalf_of_company: profileData?.acts_on_behalf_of_company === true || profileData?.acts_on_behalf_of_company === 1 || false,
      company_name: profileData?.company_name || "",
      company_tax_id: profileData?.company_tax_id || "",
    },
    validationSchema: profileUpdateSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      try {
        // Determinar si el usuario es abogado basado en el estado actual
        const isLawyer = isLawyerMode || (values.lawyer_membership_number && values.lawyer_membership_number.trim() !== "");
        
        const updateData = {
          email: values.email,
          mobile_phone: values.mobile_phone,
          lawyer_membership_number: isLawyer ? values.lawyer_membership_number : null,
          province_id: isLawyer ? values.province_id : null,
          accept_emails: values.accepts_emails,
          // Enviar datos de empresa
          acts_on_behalf_of_company: !!values.acts_on_behalf_of_company,
          company_name: values.acts_on_behalf_of_company ? values.company_name : null,
          company_tax_id: values.acts_on_behalf_of_company ? String(values.company_tax_id).toUpperCase() : null,
        };

        await updateProfile(updateData);
        setSuccessMessage("Perfil actualizado correctamente");
        
        // Refresh profile data
        const response = await getProfile();
        const userData = response.user || response;
        setProfileData(userData);
        setUser(userData);
        
        // Actualizar isLawyerMode basado en los nuevos datos
        const hasLawyerData = userData?.lawyer_membership_number && userData.lawyer_membership_number.trim() !== "";
        setIsLawyerMode(hasLawyerData);
        const hasCompanyData = userData?.acts_on_behalf_of_company === true || userData?.acts_on_behalf_of_company === 1;
        setIsCompanyMode(!!hasCompanyData);
      } catch (error) {
        setError(error.message || "Error al actualizar el perfil");
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    },
  });



  // Password change formik
  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: passwordChangeSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      try {
        await changePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        
        setSuccessMessage("Contraseña actualizada correctamente");
        passwordFormik.resetForm();
        setShowPasswordSection(false);
      } catch (error) {
        setError(error.message || "Error al cambiar la contraseña");
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const filteredProvinces = provinces.filter((province) =>
    province.name.toLowerCase().includes(provinceFilter.toLowerCase())
  );

  const handleProvinceClick = (province) => {
    profileFormik.setFieldValue("province_id", province.id);
    profileFormik.setFieldValue("province_name", province.name);
    setShowProvinceDropdown(false);
    setProvinceFilter(""); // Limpiar el filtro al seleccionar
  };

  const getSelectedProvinceName = () => {
    if (profileFormik.values.province_name) {
      return profileFormik.values.province_name;
    }

    if (profileFormik.values.province_id && provinces.length > 0) {
      const province = provinces.find(
        (p) => p.id === profileFormik.values.province_id
      );
      return province ? province.name : "";
    }

    return "";
  };

  const togglePasswordVisibility = (field) => {
    if (field === "current") {
      setShowPassword(!showPassword);
    } else if (field === "new") {
      setShowNewPassword(!showNewPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Helper function to check if user is a lawyer
  const isLawyer = () => {
    return profileFormik.values.lawyer_membership_number && profileFormik.values.lawyer_membership_number.trim() !== "";
  };

  // Handle lawyer toggle
  const handleLawyerToggle = () => {
    if (isLawyerMode) {
      // Si está en modo abogado, desactivarlo y limpiar campos
      setIsLawyerMode(false);
      profileFormik.setFieldValue("lawyer_membership_number", "");
      profileFormik.setFieldValue("province_id", "");
      profileFormik.setFieldValue("province_name", "");
    } else {
      // Si no está en modo abogado, activarlo
      setIsLawyerMode(true);
    }
  };

  // Handle company toggle
  const handleCompanyToggle = () => {
    if (isCompanyMode || profileFormik.values.acts_on_behalf_of_company) {
      setIsCompanyMode(false);
      profileFormik.setFieldValue('acts_on_behalf_of_company', false);
      profileFormik.setFieldValue('company_name', '');
      profileFormik.setFieldValue('company_tax_id', '');
    } else {
      setIsCompanyMode(true);
      profileFormik.setFieldValue('acts_on_behalf_of_company', true);
    }
  };

  // Handle logout function
  const handleLogout = async () => {
    try {
      await logoutUser();
      // Actualizar el estado de autenticación
      logout();
      // Navegar a la página de login sin forzar recarga
      navigate("/login");
    } catch (error) {
      console.error("Error al hacer logout:", error);
      // En caso de error, asegurar que el estado de autenticación se actualice
      logout();
      navigate("/login");
    }
  };

  if (!profileData) {
    return (
      <FormContainer>
        <Sidebar />
        <ContentArea>
          <FormHeader>
            <HeaderLogo src={headerLogo} alt="Header Logo" />
            <HeaderContent>
              <FormTitle>Mi perfil</FormTitle>
              <FormSubtitle>
                Actualiza tu información personal y gestiona tu cuenta
              </FormSubtitle>
            </HeaderContent>
            <CreditsCounter>
              <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
              <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
            </CreditsCounter>
          </FormHeader>

          <FormGrid>
            {/* Columna izquierda: Skeleton de campos principales */}
            <div>
              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="120px" />
                  <SkeletonLoader height="20px" width="200px" />
                </div>
              </SkeletonField>

              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="100px" />
                  <SkeletonLoader height="20px" width="180px" />
                </div>
              </SkeletonField>

              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="160px" />
                  <SkeletonLoader height="20px" width="150px" />
                </div>
              </SkeletonField>

              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="80px" />
                  <SkeletonLoader height="20px" width="220px" />
                </div>
              </SkeletonField>

              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="140px" />
                  <SkeletonLoader height="20px" width="160px" />
                </div>
              </SkeletonField>
            </div>

            {/* Columna derecha: Skeleton de información de abogado */}
            <div>
              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="130px" />
                  <SkeletonLoader height="20px" width="40px" />
                </div>
              </SkeletonField>

              <SkeletonField>
                <div className="input-row">
                  <SkeletonLoader height="20px" width="150px" />
                  <SkeletonLoader height="20px" width="40px" />
                </div>
              </SkeletonField>

              <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <SkeletonLoader height="40px" width="80px" />
                <SkeletonLoader height="40px" width="140px" />
              </div>
            </div>
          </FormGrid>
          <Footer />
        </ContentArea>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Sidebar />
      <ContentArea>
        <FormHeader>
          <HeaderLogo src={headerLogo} alt="Header Logo" />
          <HeaderContent>
            <FormTitle>Mi perfil</FormTitle>
            <FormSubtitle>
              Actualiza tu información personal y gestiona tu cuenta
            </FormSubtitle>
          </HeaderContent>
          <CreditsCounter>
            <CreditsNumber>{formatCreditsDisplay().number}</CreditsNumber>
            <CreditsLabel>{formatCreditsDisplay().label}</CreditsLabel>
          </CreditsCounter>
        </FormHeader>

        {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}

        <form onSubmit={profileFormik.handleSubmit}>
          <FormGrid>
            {/* Columna izquierda: Campos principales */}
            <div>
              {/* Read-only fields */}
              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="first_name">Nombre</Label>
                  <ReadOnlyInput
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={profileData?.first_name || ""}
                    readOnly
                    disabled
                  />
                </div>
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="last_name">Apellidos</Label>
                  <ReadOnlyInput
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={profileData?.last_name || ""}
                    readOnly
                    disabled
                  />
                </div>
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="id_number">Documento de identidad</Label>
                  <ReadOnlyInput
                    id="id_number"
                    name="id_number"
                    type="text"
                    value={profileData?.id_number || ""}
                    readOnly
                    disabled
                  />
                </div>
              </HorizontalField>

              {/* Editable fields */}
              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="email">Mail</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                    value={profileFormik.values.email}
                    error={
                      profileFormik.touched.email && profileFormik.errors.email
                    }
                  />
                </div>
                {profileFormik.touched.email && profileFormik.errors.email && (
                  <ErrorMessage>{profileFormik.errors.email}</ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="mobile_phone">Teléfono móvil</Label>
                  <Input
                    id="mobile_phone"
                    name="mobile_phone"
                    type="text"
                    onChange={profileFormik.handleChange}
                    onBlur={profileFormik.handleBlur}
                    value={profileFormik.values.mobile_phone}
                    error={
                      profileFormik.touched.mobile_phone &&
                      profileFormik.errors.mobile_phone
                    }
                  />
                </div>
                {profileFormik.touched.mobile_phone &&
                  profileFormik.errors.mobile_phone && (
                    <ErrorMessage>
                      {profileFormik.errors.mobile_phone}
                    </ErrorMessage>
                  )}
              </HorizontalField>
            </div>

            {/* Columna derecha: Información de abogado y opciones */}
            <div>
              <ToggleContainer>
                <ToggleLabel htmlFor="is_lawyer">¿Eres Abogado?</ToggleLabel>
                <ToggleSwitch
                  checked={isLawyerMode || isLawyer()}
                  onClick={handleLawyerToggle}
                >
                  {(isLawyerMode || isLawyer()) ? <NoLabel>SI</NoLabel> : <NoLabel>NO</NoLabel>}
                </ToggleSwitch>
              </ToggleContainer>

              {(isLawyerMode || isLawyer()) && (
                <>
                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="lawyer_membership_number">
                        Número de colegiado
                      </Label>
                      <Input
                        id="lawyer_membership_number"
                        name="lawyer_membership_number"
                        type="text"
                        onChange={profileFormik.handleChange}
                        onBlur={profileFormik.handleBlur}
                        value={profileFormik.values.lawyer_membership_number}
                        error={
                          profileFormik.touched.lawyer_membership_number &&
                          profileFormik.errors.lawyer_membership_number
                        }
                      />
                    </div>
                    {profileFormik.touched.lawyer_membership_number &&
                      profileFormik.errors.lawyer_membership_number && (
                        <ErrorMessage>
                          {profileFormik.errors.lawyer_membership_number}
                        </ErrorMessage>
                      )}
                  </HorizontalField>

                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="province_id" hideOnMobile>
                        Indica la localidad del colegio
                      </Label>
                      <ProvinceDropdown ref={provinceDropdownRef}>
                        <SearchInput
                          type="text"
                          placeholder="Buscar provincia..."
                          onClick={() => setShowProvinceDropdown(true)}
                          onChange={(e) => setProvinceFilter(e.target.value)}
                          value={
                            showProvinceDropdown
                              ? provinceFilter
                              : getSelectedProvinceName()
                          }
                          onFocus={() => setShowProvinceDropdown(true)}
                          onBlur={() => {
                            setTimeout(() => {
                              if (
                                !provinceDropdownRef.current?.contains(
                                  document.activeElement
                                )
                              ) {
                                setShowProvinceDropdown(false);
                                setProvinceFilter(""); // Limpiar filtro al cerrar
                              }
                            }, 300); // Aumentar el timeout para dar más tiempo
                          }}
                          error={
                            profileFormik.touched.province_id &&
                            profileFormik.errors.province_id
                          }
                        />
                        {showProvinceDropdown && (
                          <DropdownList>
                            {filteredProvinces.map((province) => (
                              <DropdownItem
                                key={province.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleProvinceClick(province);
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevenir que el input pierda el foco
                                }}
                              >
                                {province.name}
                              </DropdownItem>
                            ))}
                          </DropdownList>
                        )}
                      </ProvinceDropdown>
                    </div>
                    {profileFormik.touched.province_id &&
                      profileFormik.errors.province_id && (
                        <ErrorMessage>
                          {profileFormik.errors.province_id}
                        </ErrorMessage>
                      )}
                  </HorizontalField>
                </>
              )}

              {/* Toggle de sociedad */}
              <ToggleContainer>
                <ToggleLabel htmlFor="acts_on_behalf_of_company">¿Actúas en nombre de una sociedad?</ToggleLabel>
                <ToggleSwitch
                  checked={profileFormik.values.acts_on_behalf_of_company}
                  onClick={handleCompanyToggle}
                >
                  {profileFormik.values.acts_on_behalf_of_company ? <NoLabel>SI</NoLabel> : <NoLabel>NO</NoLabel>}
                </ToggleSwitch>
              </ToggleContainer>

              {profileFormik.values.acts_on_behalf_of_company && (
                <>
                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="company_name">Razón social</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        type="text"
                        placeholder="Empresa, S.L"
                        onChange={profileFormik.handleChange}
                        onBlur={profileFormik.handleBlur}
                        value={profileFormik.values.company_name}
                        error={
                          profileFormik.touched.company_name && profileFormik.errors.company_name
                        }
                      />
                    </div>
                    {profileFormik.touched.company_name && profileFormik.errors.company_name && (
                      <ErrorMessage>{profileFormik.errors.company_name}</ErrorMessage>
                    )}
                  </HorizontalField>

                  <HorizontalField>
                    <div className="input-row">
                      <Label htmlFor="company_tax_id">NIF / CIF</Label>
                      <Input
                        id="company_tax_id"
                        name="company_tax_id"
                        type="text"
                        placeholder="B1234567C"
                        onChange={(e) => {
                          const upper = e.target.value.toUpperCase();
                          profileFormik.setFieldValue('company_tax_id', upper);
                        }}
                        onBlur={profileFormik.handleBlur}
                        value={profileFormik.values.company_tax_id}
                        error={
                          profileFormik.touched.company_tax_id && profileFormik.errors.company_tax_id
                        }
                      />
                    </div>
                    {profileFormik.touched.company_tax_id && profileFormik.errors.company_tax_id && (
                      <ErrorMessage>{profileFormik.errors.company_tax_id}</ErrorMessage>
                    )}
                  </HorizontalField>
                </>
              )}

              <ToggleContainer>
                <ToggleLabel htmlFor="accepts_emails">
                  Acepto recibir emails
                </ToggleLabel>
                <ToggleSwitch
                  checked={profileFormik.values.accepts_emails}
                  onClick={() =>
                    profileFormik.setFieldValue(
                      "accepts_emails",
                      !profileFormik.values.accepts_emails
                    )
                  }
                >
                  {profileFormik.values.accepts_emails ? null : (
                    <NoLabel>NO</NoLabel>
                  )}
                </ToggleSwitch>
              </ToggleContainer>

              <ButtonContainer>
                <LogoutButton type="button" onClick={handleLogout}>
                  Cerrar sesión
                </LogoutButton>
                <PrimaryButton type="submit" disabled={loading}>
                  Guardar cambios
                </PrimaryButton>
              </ButtonContainer>
            </div>
          </FormGrid>
        </form>

        {/* Password change section */}
        <SectionTitle>Cambiar contraseña</SectionTitle>
        {!showPasswordSection ? (
          <ButtonContainer style={{ justifyContent: "flex-start" }}>
            <PrimaryButton
              type="button"
              onClick={() => setShowPasswordSection(true)}
            >
              Cambiar contraseña
            </PrimaryButton>
          </ButtonContainer>
        ) : (
          <form onSubmit={passwordFormik.handleSubmit}>
            <FormGrid>
              <div>
                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="currentPassword">Contraseña actual</Label>
                    <PasswordField>
                      <PasswordInput
                        id="currentPassword"
                        name="currentPassword"
                        type={showPassword ? "text" : "password"}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        value={passwordFormik.values.currentPassword}
                        error={
                          passwordFormik.touched.currentPassword &&
                          passwordFormik.errors.currentPassword
                        }
                      />
                      <EyeIcon
                        onClick={() => togglePasswordVisibility("current")}
                      >
                        <EyeIconSvg isVisible={!showPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {passwordFormik.touched.currentPassword &&
                    passwordFormik.errors.currentPassword && (
                      <ErrorMessage>
                        {passwordFormik.errors.currentPassword}
                      </ErrorMessage>
                    )}
                </HorizontalField>

                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="newPassword">Nueva contraseña</Label>
                    <PasswordField>
                      <PasswordInput
                        id="newPassword"
                        name="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        value={passwordFormik.values.newPassword}
                        error={
                          passwordFormik.touched.newPassword &&
                          passwordFormik.errors.newPassword
                        }
                      />
                      <EyeIcon
                        onClick={() => togglePasswordVisibility("new")}
                      >
                        <EyeIconSvg isVisible={!showNewPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {passwordFormik.touched.newPassword &&
                    passwordFormik.errors.newPassword && (
                      <ErrorMessage>
                        {passwordFormik.errors.newPassword}
                      </ErrorMessage>
                    )}
                </HorizontalField>

                <HorizontalField>
                  <div className="input-row">
                    <Label htmlFor="confirmNewPassword">
                      Repetir nueva contraseña
                    </Label>
                    <PasswordField>
                      <PasswordInput
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        value={passwordFormik.values.confirmNewPassword}
                        error={
                          passwordFormik.touched.confirmNewPassword &&
                          passwordFormik.errors.confirmNewPassword
                        }
                      />
                      <EyeIcon
                        onClick={() => togglePasswordVisibility("confirm")}
                      >
                        <EyeIconSvg isVisible={!showConfirmPassword} />
                      </EyeIcon>
                    </PasswordField>
                  </div>
                  {passwordFormik.touched.confirmNewPassword &&
                    passwordFormik.errors.confirmNewPassword && (
                      <ErrorMessage>
                        {passwordFormik.errors.confirmNewPassword}
                      </ErrorMessage>
                    )}
                </HorizontalField>
                
                <ButtonContainer>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      setShowPasswordSection(false);
                      passwordFormik.resetForm();
                    }}
                  >
                    Cancelar
                  </SecondaryButton>
                  <PrimaryButton type="submit" disabled={loading}>
                    Cambiar contraseña
                  </PrimaryButton>
                </ButtonContainer>
              </div>
              <div>
                {/* Empty column for grid layout */}
              </div>
            </FormGrid>
          </form>
        )}

        {/* Error modal */}
        {showErrorModal && error && (
          <ErrorModalOverlay onClick={() => setShowErrorModal(false)}>
            <ErrorModalContent onClick={(e) => e.stopPropagation()}>
              <ErrorModalHeader>
                <ErrorModalTitle>Error</ErrorModalTitle>
                <ErrorModalCloseButton onClick={() => setShowErrorModal(false)}>
                  ×
                </ErrorModalCloseButton>
              </ErrorModalHeader>
              <ErrorModalMessage>{error}</ErrorModalMessage>
            </ErrorModalContent>
          </ErrorModalOverlay>
        )}
        <Footer />
        

      </ContentArea>
    </FormContainer>
  );
};

export default ProfileEditPage; 